import { useCallback, useEffect, useRef, useState } from 'react';
import type { CardId, Move, Seat } from '@basgan/rules';
import { FALLBACK_CHAT_PHRASES, type GameEvent, type PublicSeat, type ViewMessage } from '@basgan/protocol';
import {
  applyEvent,
  eventDuration,
  initialDisplay,
  reconcile,
  type DisplayState,
} from '../game/display.js';
import { shouldAutoPass } from '../game/autoPass.js';
import { saveBotMatch } from './stats.js';
import { createLocalGame, randomSeed } from './localGame.js';
import { connectOnline } from './online.js';
import type { TablePlayer, Transport, TransportHandlers } from './transport.js';

export type Status = 'idle' | 'connecting' | 'lobby' | 'playing' | 'over' | 'error';

/** Oynayacak kartı olmayan oyuncu bu süre sonunda otomatik pas geçer. */
const AUTO_PASS_MS = 700;
/** Otomatik pas tercihi cihazda kalıyor: her maçta yeniden ayarlamak gerekmesin. */
const AUTO_PASS_KEY = 'basgan:autopass';
/** Bir maçtaki toplam el — istatistiğe yazarken kullanılıyor (4 set × 4 el). */
const TOTAL_DEALS = 16;
/** Hazır mesaj balonunun ekranda kalma süresi. */
const CHAT_VISIBLE_MS = 3600;
/** Değişim sonucu perdesinin ekranda kalma süresi. */
const EXCHANGE_RESULT_MS = 2400;
export type Mode = 'local' | 'online';

export interface GameApi {
  status: Status;
  mode: Mode | null;
  error: string | null;
  /** Yetkili durum — geçerli hamleler, faz, skor buradan okunur. */
  view: ViewMessage | null;
  /** Ekranda görünen durum — animasyon kuyruğu kadar geride. */
  display: DisplayState;
  seats: PublicSeat[];
  waitingFor: number;
  /** Masada oturanlar (kartlar dağıtılmadan önce) — bot koltukları dahil. */
  tablePlayers: TablePlayer[];
  /** Masaya ilk oturan bu istemci mi — boş koltuğa bot ekleyebilen tek kişi. */
  isHost: boolean;
  /** Masadaki bot koltuğu sayısını AYARLAR (mutlak değer, artırma değil). */
  setBots(count: number): void;
  /** Masa doldu; maçın başlayacağı mutlak an (epoch ms). Yoksa null. */
  startsAt: number | null;
  /** Kuyruk oynarken girdi kilitlenir; yarım animasyona hamle gönderilmez. */
  busy: boolean;
  /** Oynanacak kart yok, otomatik pas geçiliyor — arayüz bunu duyuruyor. */
  autoPassing: boolean;
  /** Otomatik pas açık mı — kapalıyken oynayacak kart yokken de oyuncu bekleniyor. */
  autoPassEnabled: boolean;
  toggleAutoPass(): void;
  /** "Ver" gönderildi, sunucu onayı bekleniyor — arayüz hemen tepki versin. */
  exchangeSent: boolean;
  /** Değişim sonucu perdesi ekranda mı? İçeriği `view.lastExchange`'de. */
  exchangeResult: boolean;
  /** Şu an ekranda duran hazır mesajlar: koltuk → cümle. */
  chat: Partial<Record<Seat, string>>;
  /** Seçilebilir hazır mesajlar — sunucudan, yoksa gömülü yedek. */
  phrases: string[];
  sendChat(text: string): void;
  /**
   * Maç sonu kararları — masadaki koltuk numarasıyla.
   *
   * `sold` sırayla birikiyor: "Alper şerefsizi sattı" satırları geliş
   * sırasında alt alta yazılıyor.
   */
  sold: { seat: Seat; name: string }[];
  stayed: Seat[];
  /**
   * Maç sonu sıralaması — `matchOver` olayından, görünümden DEĞİL.
   *
   * Görünüm animasyon kuyruğunun gerisinden geliyor: son elin puanları
   * ekrana işlenmeden panel açılıyor ve tablo bir an "0 - 0 - 0 - 0"
   * gösteriyordu. Olayın kendisi yetkili ve anında elimizde.
   */
  standings: { seat: Seat; score: number; place: number }[];
  /** "Kal ya da sat" kararının son anı (epoch ms). Yerel oyunda null. */
  decideBy: number | null;
  /** Masada kal — dördü de kalırsa yeni maç geri sayımı başlar. */
  stay(): void;
  /** Masadan kalk. Çevrimiçi masada kalanlara duyurusu gider. */
  sell(): void;
  /**
   * Maskot turu sayacı — her artışta animasyon baştan koşuyor.
   *
   * Sayaç, çünkü art arda gelen iki tur ayırt edilebilmeli; bayrak olsaydı
   * ikinci tur hiç görünmezdi.
   */
  mascotRun: number;
  /** Maskotu masaya salar. Sunucu yalnızca yöneticiden kabul ediyor. */
  runMascot(): void;
  startLocal(name: string): void;
  connect(name: string, code: string): Promise<void>;
  play(cards: CardId[]): void;
  pass(): void;
  submitExchange(cards: CardId[]): void;
  quit(): void;
}

export function useGame(): GameApi {
  const [status, setStatus] = useState<Status>('idle');
  const [mode, setMode] = useState<Mode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<ViewMessage | null>(null);
  const [display, setDisplay] = useState<DisplayState>(initialDisplay);
  const [seats, setSeats] = useState<PublicSeat[]>([]);
  const [waitingFor, setWaitingFor] = useState(0);
  /** Masada oturanların adları — kartlar dağıtılmadan önceki masa görünümü. */
  const [tablePlayers, setTablePlayers] = useState<TablePlayer[]>([]);
  const [isHost, setIsHost] = useState(false);
  /** Maç sonu: kim kalktı, kim kaldı, kararın son anı. */
  const [sold, setSold] = useState<{ seat: Seat; name: string }[]>([]);
  const [stayed, setStayed] = useState<Seat[]>([]);
  const [decideBy, setDecideBy] = useState<number | null>(null);
  const [standings, setStandings] = useState<{ seat: Seat; score: number; place: number }[]>([]);
  const [mascotRun, setMascotRun] = useState(0);
  const [busy, setBusy] = useState(false);
  const [autoPassing, setAutoPassing] = useState(false);
  /**
   * Otomatik pas açık mı.
   *
   * VARSAYILAN AÇIK: otomatik pas bu düğme gelmeden önce de vardı ve hep
   * çalışıyordu. Kapalı başlatmak, güncellemeyle birlikte herkesin davranışını
   * sessizce değiştirirdi.
   */
  const [autoPassEnabled, setAutoPassEnabled] = useState(() => {
    try {
      return localStorage.getItem(AUTO_PASS_KEY) !== '0';
    } catch {
      return true; // gizli sekmede depolama kapalı olabilir
    }
  });
  const toggleAutoPass = useCallback(() => {
    setAutoPassEnabled((on) => {
      const next = !on;
      try {
        localStorage.setItem(AUTO_PASS_KEY, next ? '1' : '0');
      } catch {
        /* depolama yoksa tercih yalnızca bu oturumda geçerli */
      }
      return next;
    });
  }, []);
  /** Masa doldu; maçın başlayacağı mutlak an (epoch ms). Yoksa null. */
  const [startsAt, setStartsAt] = useState<number | null>(null);

  const transport = useRef<Transport | null>(null);
  const queue = useRef<GameEvent[]>([]);
  const pendingView = useRef<ViewMessage | null>(null);
  const draining = useRef(false);
  const me = useRef<Seat>(0);
  /** Yerel maçın seed'i. Dolu olması "bu maç botlara karşı" demek. */
  const localSeed = useRef<string | null>(null);
  /** Son kullanılan ad — botlara karşı "kal oyna" yeni maçı bununla kuruyor. */
  const myName = useRef('Sen');
  /**
   * Gönderilen değişim isteği işlenene kadar aradan gelen `view` hâlâ
   * `submitted: false` gösterir. Uçuştaki isteği burada takip etmezsek kartlar
   * iki kez gönderilir — sunucu entegrasyon testinde yakalanan durum.
   */
  const exchangeSentForDeal = useRef(-1);
  /** İlk görünüm geldi mi — geldiyse kuyruk kuralı yeniden geçerli. */
  const hasView = useRef(false);
  /**
   * "Ver" basıldı ama sunucu henüz onaylamadı. Panel bunu beklerken seçim
   * ekranını gösteriyordu ve düğme tepkisiz görünüyordu.
   */
  const [exchangeSentDeal, setExchangeSentDeal] = useState(-1);
  /**
   * Sonuç perdesi KUYRUĞUN DIŞINDA, kendi sayacıyla. Kuyruğa bağlıyken perdenin
   * süresi, içeriğini taşıyan görünüm gelmeden önce tükeniyordu.
   */
  const [exchangeResult, setExchangeResult] = useState(false);
  const exchangeResultTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Değişim bitti, perde görünüm uygulanır uygulanmaz açılacak. */
  const exchangeResultPending = useRef(false);

  /**
   * Ekranda duran hazır mesajlar — koltuk başına bir balon, kendi süresiyle.
   * Kuyruğun dışında yaşıyor: sohbet oyun durumundan bağımsız.
   */
  const [chat, setChat] = useState<Partial<Record<Seat, string>>>({});
  /**
   * Hazır mesaj listesi. Sunucudan geliyor (cümleler yeni sürüm çıkmadan
   * değişebilsin diye); offline bot oyununda sunucu yok, gömülü yedek kalıyor.
   *
   * `reset()` bunu BİLEREK sıfırlamıyor: bir kez online oynadıysan güncel liste
   * elinde kalıyor ve sonraki offline oyunda da onu görüyorsun.
   */
  const [phrases, setPhrases] = useState<string[]>([...FALLBACK_CHAT_PHRASES]);
  const chatTimers = useRef(new Map<Seat, ReturnType<typeof setTimeout>>());

  const showChat = useCallback((seat: Seat, text: string) => {
    clearTimeout(chatTimers.current.get(seat));
    setChat((current) => ({ ...current, [seat]: text }));
    chatTimers.current.set(
      seat,
      setTimeout(() => {
        setChat((current) => {
          const next = { ...current };
          delete next[seat];
          return next;
        });
        chatTimers.current.delete(seat);
      }, CHAT_VISIBLE_MS),
    );
  }, []);

  /** Kuyruğu sırayla oynatır; bitince yetkili görünüme oturur. */
  const drain = useCallback(async () => {
    if (draining.current) return;
    draining.current = true;
    setBusy(true);

    while (queue.current.length > 0) {
      const event = queue.current.shift()!;
      setDisplay((current) => applyEvent(current, event, me.current));
      const wait = eventDuration(event);
      if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
    }

    const latest = pendingView.current;
    if (latest) {
      pendingView.current = null;
      setView(latest);
      setDisplay((current) => reconcile(current, latest));
      setSeats(latest.seats);

      // Değişim sonucu perdesi: içeriği taşıyan görünüm ancak BURADA
      // uygulanıyor, o yüzden sayaç da burada başlıyor.
      if (exchangeResultPending.current && latest.lastExchange) {
        exchangeResultPending.current = false;
        if (exchangeResultTimer.current) clearTimeout(exchangeResultTimer.current);
        setExchangeResult(true);
        exchangeResultTimer.current = setTimeout(
          () => setExchangeResult(false),
          EXCHANGE_RESULT_MS,
        );
      }
    }

    draining.current = false;
    setBusy(false);
  }, []);

  const handlers = useRef<TransportHandlers>({
    event(event) {
      /**
       * Sohbet KUYRUĞA GİRMİYOR. Kuyruk kart animasyonlarını sıraya diziyor;
       * mesaj oranın arkasında beklerse saniyeler sonra beliriyor ve neye
       * cevap verildiği anlaşılmıyor. Oyun durumuna da dokunmuyor.
       */
      if (event.kind === 'chat') {
        showChat(event.seat, event.text);
        return;
      }
      /**
       * Maskot ve maç sonu kararları da KUYRUĞA GİRMİYOR.
       *
       * Maskot masanın üstünde kendi başına koşuyor, kararlar ise maç
       * bittikten sonra geliyor: ikisinin de bekleyecek bir animasyonu yok,
       * kuyruğa girselerdi el sonu özetinin arkasında saniyelerce beklerlerdi.
       */
      if (event.kind === 'mascot') {
        setMascotRun((n) => n + 1);
        return;
      }
      if (event.kind === 'stayed') {
        setStayed((current) => (current.includes(event.seat) ? current : [...current, event.seat]));
        return;
      }
      if (event.kind === 'sold') {
        setSold((current) =>
          current.some((s) => s.seat === event.seat)
            ? current
            : [...current, { seat: event.seat, name: event.name }],
        );
        return;
      }
      if (event.kind === 'matchStart') {
        setSeats(event.seats);
        setStatus('playing');
        setStartsAt(null);
        // Yeni maç: bir öncekinin kal/sat duyuruları ekranda kalmasın.
        setSold([]);
        setStayed([]);
        setDecideBy(null);
      }
      if (event.kind === 'matchOver') {
        setStatus('over');
        setDecideBy(event.decideBy ?? null);
        setStandings(event.standings);
        setSold([]);
        setStayed([]);
        /**
         * Botlara karşı oynanan maç istatistiğe BURADAN yazılıyor; online
         * maçları sunucu kendisi yazıyor. `localSeed` yalnızca yerel oyunda
         * dolu olduğu için modu ayırt etmeye de yarıyor.
         *
         * Yalnızca kendi sonucumuz yazılıyor: diğer üç koltuk bot, hesapları
         * yok. Veritabanı kuralı da zaten kendi satırından fazlasına izin
         * vermiyor.
         */
        const seed = localSeed.current;
        const mine = seed ? event.standings.find((s) => s.seat === me.current) : undefined;
        if (seed && mine) {
          void saveBotMatch({
            seed,
            deals: TOTAL_DEALS,
            seat: mine.seat,
            score: mine.score,
            place: mine.place,
          });
        }
      }
      if (event.kind === 'exchangeDone') {
        /**
         * Perde HEMEN açılmıyor, içeriği hazır olunca açılıyor.
         *
         * Sonuç perdesinin içeriği `view.lastExchange`'de geliyor ve görünüm
         * ancak animasyon kuyruğu boşalınca uygulanıyor. Perde olay gelir
         * gelmez açılınca aradaki yarışı kaybediyordu: ekranda boş perde
         * duruyor, 2400 ms doluyor, kapanıyor ve kartı veren oyuncu ne
         * aldığını hiç göremiyordu. Artık `drain` sonunda açılıyor.
         */
        exchangeResultPending.current = true;
      }
      queue.current.push(event);
      void drain();
    },
    view(incoming) {
      me.current = incoming.you;
      /**
       * Maskot düğmesi maç sürerken de yalnızca yöneticide görünsün.
       *
       * Yerel oyunda (`localSeed` dolu) yönetici zaten sensin: sunucu yok,
       * görünümde `host` alanı da yok, ama düğmenin kaybolması için sebep de
       * yok — masadaki tek insan sensin.
       */
      setIsHost(incoming.host === true || localSeed.current !== null);
      // Her zaman en yenisini sakla; kuyruk boşalınca ona oturulacak.
      if (!pendingView.current || incoming.seq > pendingView.current.seq) {
        pendingView.current = incoming;
      }

      /**
       * İLK görünüm kuyruğu beklemez. Kuyruğun bitmesini beklemenin sebebi
       * yarım animasyonun üstüne yazmamak; ortada henüz bir animasyon durumu
       * yokken beklemek yalnızca boş ekran demek. Maç başlarken masa
       * saniyelerce gelmiyordu.
       */
      if (!hasView.current) {
        hasView.current = true;
        pendingView.current = null;
        setView(incoming);
        setDisplay((current) => reconcile(current, incoming));
        setSeats(incoming.seats);
      }

      setStatus((s) => (s === 'lobby' || s === 'connecting' ? 'playing' : s));
      void drain();
    },
    error(message) {
      setError(message);
      setTimeout(() => setError(null), 2600);
    },
    lobby(players, needed, host) {
      setTablePlayers(players);
      setWaitingFor(needed);
      setIsHost(host);
      setStatus('lobby');
    },
    starting(at) {
      setStartsAt(at);
    },
    phrases(list) {
      if (list.length > 0) setPhrases(list);
    },
  });

  const reset = useCallback(() => {
    transport.current?.dispose();
    transport.current = null;
    queue.current = [];
    pendingView.current = null;
    draining.current = false;
    exchangeSentForDeal.current = -1;
    hasView.current = false;
    setExchangeSentDeal(-1);
    if (exchangeResultTimer.current) clearTimeout(exchangeResultTimer.current);
    exchangeResultTimer.current = null;
    setExchangeResult(false);
    for (const timer of chatTimers.current.values()) clearTimeout(timer);
    chatTimers.current.clear();
    setChat({});
    setStartsAt(null);
    setTablePlayers([]);
    setSold([]);
    setStayed([]);
    setDecideBy(null);
    setDisplay(initialDisplay());
    setView(null);
    setSeats([]);
    setError(null);
  }, []);

  const startLocal = useCallback(
    (name: string) => {
      reset();
      setMode('local');
      setStatus('connecting');
      myName.current = name || 'Sen';
      // Seed'i BURADA üretiyoruz ki maç bitince istatistiğe yazabilelim;
      // dolu olması aynı zamanda "bu maç yerel" işareti.
      const seed = randomSeed();
      localSeed.current = seed;
      transport.current = createLocalGame({ name: name || 'Sen', seed }, handlers.current);
    },
    [reset],
  );

  const connect = useCallback(
    async (name: string, code: string) => {
      reset();
      setMode('online');
      setStatus('connecting');
      myName.current = name;
      // Online maçın sonucunu sunucu yazıyor; yerel seed işareti temizleniyor.
      localSeed.current = null;
      try {
        transport.current = await connectOnline({ name, code }, handlers.current);
        setStatus('lobby');
      } catch (cause) {
        setStatus('error');
        setError(cause instanceof Error ? cause.message : 'Bağlanılamadı');
      }
    },
    [reset],
  );

  const send = useCallback(
    (
      type: 'move' | 'exchange' | 'chat' | 'hello' | 'bots' | 'stay' | 'sell' | 'mascot',
      payload: unknown,
    ) => {
      transport.current?.send(type, payload);
    },
    [],
  );

  const sendChat = useCallback((text: string) => send('chat', { text }), [send]);

  const setBots = useCallback((count: number) => send('bots', { count }), [send]);

  const runMascot = useCallback(() => send('mascot', {}), [send]);

  const play = useCallback(
    (cards: CardId[]) => send('move', { type: 'play', cards } satisfies Move),
    [send],
  );
  const pass = useCallback(() => send('move', { type: 'pass' } satisfies Move), [send]);

  const submitExchange = useCallback(
    (cards: CardId[]) => {
      const dealIndex = view?.dealIndex ?? -1;
      if (exchangeSentForDeal.current === dealIndex) return;
      exchangeSentForDeal.current = dealIndex;
      setExchangeSentDeal(dealIndex);
      send('exchange', { cards });
    },
    [send, view?.dealIndex],
  );

  /**
   * Oynayacak geçerli kartı olmayan oyuncuyu bekletmenin anlamı yok: tek
   * seçeneği pas. Kuyruk boşalana kadar (`busy`) beklenip kısa bir duyuruyla
   * geçiliyor — yoksa animasyon bitmeden pas geçilip ne olduğu anlaşılmıyor.
   */
  const autoPassedSeq = useRef(-1);
  useEffect(() => {
    if (!view || busy) return;
    if (autoPassedSeq.current === view.seq) return;

    /**
     * Otomatik pas KAPALIYSA hiç karışmıyoruz.
     *
     * Oynayacak kartın olmadığı durumda bile pas kararını oyuncu veriyor;
     * ekranda ne olduğunu görmek isteyen için bu önemli. Kapalıyken tek
     * çıkış oyuncunun "Pas" demesi ya da sürenin dolması.
     */
    if (!autoPassEnabled) return;
    if (!shouldAutoPass(view)) return;

    autoPassedSeq.current = view.seq;
    setAutoPassing(true);
    const id = setTimeout(() => {
      setAutoPassing(false);
      pass();
    }, AUTO_PASS_MS);
    return () => clearTimeout(id);
  }, [view, busy, pass, autoPassEnabled]);

  const quit = useCallback(() => {
    reset();
    setMode(null);
    setStatus('idle');
  }, [reset]);

  const stay = useCallback(() => {
    if (mode === 'local') {
      // Botlara karşı oynarken beklenecek kimse yok: yeni maç hemen başlıyor.
      startLocal(myName.current);
      return;
    }
    send('stay', {});
  }, [mode, send, startLocal]);

  /**
   * "Sat ve git": önce DUYURU, sonra kapanış.
   *
   * Sıra önemli — `quit()` bağlantıyı kapatıyor, mesaj arkasından
   * gönderilseydi hiç ulaşmazdı ve kalanların ekranında kimin kalktığı
   * yazmazdı.
   */
  const sell = useCallback(() => {
    if (mode === 'online') send('sell', {});
    quit();
  }, [mode, quit, send]);

  useEffect(() => () => transport.current?.dispose(), []);

  return {
    status,
    mode,
    error,
    view,
    display,
    seats,
    waitingFor,
    tablePlayers,
    isHost,
    setBots,
    startsAt,
    busy,
    autoPassing,
    autoPassEnabled,
    toggleAutoPass,
    exchangeSent: view !== null && view.phase === 'exchange' && exchangeSentDeal === view.dealIndex,
    exchangeResult,
    chat,
    phrases,
    sendChat,
    sold,
    stayed,
    standings,
    decideBy,
    stay,
    sell,
    mascotRun,
    runMascot,
    startLocal,
    connect,
    play,
    pass,
    submitExchange,
    quit,
  };
}
